import { defineComponent } from 'vue';
import type { RouteRecordRaw } from 'vue-router';

import App from '#nora-ui/App.vue';
import { RouteName } from '#nora-ui/router/route-names';

// Leaf route components are lazily loaded so the settings/admin subtrees are
// code-split out of the initial bundle. App.vue is the persistent layout, so it
// is imported eagerly.
const SimulatorApp = async () => await import('#nora-ui/simulator/SimulatorApp.vue');

const OperationsPanel = async () => await import('#nora-ui/components/operations/OperationsPanel.vue');
const GroupsPanel = async () => await import('#nora-ui/components/groups/GroupsPanel.vue');
const FilesPanel = async () => await import('#nora-ui/components/files/FilesPanel.vue');
const NotificationsPanel = async () => await import('#nora-ui/components/notifications/NotificationsPanel.vue');

const SettingsPanel = async () => await import('#nora-ui/components/settings/SettingsPanel.vue');
const Settings = async () => await import('#nora-ui/components/settings/Settings.vue');
const ConsentSettingsPanel = async () => await import('#nora-ui/components/settings/permission/consent/ConsentPermissionSettings.vue');
const MapCoordinateSettings = async () => await import('#nora-ui/components/settings/map/MapCoordinateSettings.vue');
const ChangelogSettingsPanel = async () => await import('#nora-ui/components/settings/changelog/ChangelogSettingsPanel.vue');
const WorkspacePicker = async () => await import('#nora-ui/components/settings/workspace/WorkspacePicker.vue');

const AdminWrapper = async () => await import('#nora-ui/components/admin/AdminWrapper.vue');
const AdminUserPanel = async () => await import('#nora-ui/components/admin/user/AdminUserPanel.vue');
const AdminGroupPanel = async () => await import('#nora-ui/components/admin/group/AdminGroupPanel.vue');
const AdminTrackerPanel = async () => await import('#nora-ui/components/admin/tracker/AdminTrackerPanel.vue');
const AdminOperationPanel = async () => await import('#nora-ui/components/admin/operation/AdminOperationPanel.vue');
const AdminConversationPanel = async () => await import('#nora-ui/components/admin/conversation/AdminConversationPanel.vue');
const AdminDrawLayerPanel = async () => await import('#nora-ui/components/admin/draw-layer/AdminDrawLayerPanel.vue');
const AdminConfigurationPanel = async () => await import('#nora-ui/components/admin/configuration/AdminConfigurationPanel.vue');
const AdminRolesPermissionsPanel = async () => await import('#nora-ui/components/admin/roles-permissions/AdminRolesPermissionsPanel.vue');
const MapSettingsPanel = async () => await import('#nora-ui/components/admin/map-settings/MapSettingsPanel.vue');
const AdminSymbolPackPanel = async () => await import('#nora-ui/components/admin/symbol-pack/AdminSymbolPackPanel.vue');
const NexusAdminGraph = async () => await import('#nora-ui/components/admin/nexus/NexusAdminGraph.vue');

// NoraChat is rendered always-mounted in App.vue rather than via this route, so
// /chat just needs an (empty) panel outlet to be navigable. This is to make sure the chat panel's open/closed state is preserved across
// route navigations, and to make sure SSE connections stay alive even when the user navigates away from /chat
const ChatPanelOutlet = defineComponent({
  name: 'ChatPanelOutlet',
  render: () => null,
});

export const routes: RouteRecordRaw[] = [
  {
    // App is the persistent layout: the map backdrop, the `panel` named view as
    // a left sidebar, and the `overlay` named view (admin) on top.
    path: '/',
    component: App,
    children: [
      { path: 'chat', name: RouteName.Chat, components: { panel: ChatPanelOutlet } },
      { path: 'operations/:operationId?', name: RouteName.Operations, components: { panel: OperationsPanel } },
      // `groupIds` is the nested-group breadcrumb stack, deepest group last.
      { path: 'groups/:groupIds*', name: RouteName.Groups, components: { panel: GroupsPanel } },
      // `folderPath` mirrors the folder `fullPath` segments (e.g.
      // /files/operation/<operationId>/<subfolder>); the selected file rides in
      // the `file*` query params (see FilesPanel.vue).
      { path: 'files/:folderPath*', name: RouteName.Files, components: { panel: FilesPanel } },
      { path: 'notifications', name: RouteName.Notifications, components: { panel: NotificationsPanel } },
      {
        path: 'settings',
        components: { panel: SettingsPanel },
        children: [
          { path: '', name: RouteName.Settings, component: Settings },
          { path: 'consent', name: RouteName.SettingsConsent, component: ConsentSettingsPanel },
          { path: 'map', name: RouteName.SettingsMap, component: MapCoordinateSettings },
          { path: 'changelog', name: RouteName.SettingsChangelog, component: ChangelogSettingsPanel },
          { path: 'workspace', name: RouteName.SettingsWorkspace, component: WorkspacePicker },
        ],
      },
      {
        path: 'admin',
        components: { overlay: AdminWrapper },
        redirect: '/admin/users',
        children: [
          { path: 'users/:userId?', name: RouteName.AdminUsers, component: AdminUserPanel },
          { path: 'groups/:groupId?', name: RouteName.AdminGroups, component: AdminGroupPanel },
          { path: 'trackers/:trackerId?', name: RouteName.AdminTrackers, component: AdminTrackerPanel },
          { path: 'operations/:operationId?', name: RouteName.AdminOperations, component: AdminOperationPanel },
          { path: 'conversations/:conversationId?', name: RouteName.AdminConversations, component: AdminConversationPanel },
          { path: 'draw-layers/:drawLayerId?', name: RouteName.AdminDrawLayers, component: AdminDrawLayerPanel },
          { path: 'configuration/:configurationIdentifier?', name: RouteName.AdminConfiguration, component: AdminConfigurationPanel },
          // `section` selects the roles/permissions category; `roleId` selects an
          // item within the global/local role lists (permissions have no detail).
          { path: 'roles/:section?/:roleId?', name: RouteName.AdminRoles, component: AdminRolesPermissionsPanel },
          { path: 'map-settings', name: RouteName.AdminMapSettings, component: MapSettingsPanel },
          { path: 'symbol-packs/:symbolPackId?', name: RouteName.AdminSymbolPacks, component: AdminSymbolPackPanel },
          { path: 'nexus', name: RouteName.AdminNexus, component: NexusAdminGraph },
        ],
      },
    ],
  },
  { path: '/simulator', component: SimulatorApp, meta: { skipBootstrap: true } },
  { path: '/:catchAll(.*)', redirect: '/' },
];
